import React from "react";
import { TransparentCard, CardHeader, CardTitle, CardContent, CardDescription } from "./ui/card";
import { Button } from "./ui/button";
import { Download } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "./ui/accordion";

export interface UpdateInstaller {
  Platform: string;
  Architecture: string;
  FileName: string;
  SHA256?: string;
}

export interface UpdateManifest {
  Version: string;
  Url: string;
  Changelog: string;
  Installers: UpdateInstaller[];
}

export interface ProductConfig {
  name: string;
  description: string;
  manifestPath: string;
  signature?: string;
  countersignature?: string;
}

interface DownloadCardProps {
  product: ProductConfig;
  detected: { os: string; arch: string };
}

export const DownloadCard: React.FC<DownloadCardProps> = ({ product, detected }) => {
  const [manifest, setManifest] = React.useState<UpdateManifest | null>(null);
  const [downloadUrl, setDownloadUrl] = React.useState<string | null>(null);
  const [selectedInstaller, setSelectedInstaller] = React.useState<UpdateInstaller | null>(null);
  const [loading, setLoading] = React.useState(true);

  const normalize = (v?: string) => (v || "unknown").toLowerCase();

  const buildUrl = (man: UpdateManifest, inst: UpdateInstaller) => {
    const base = man.Url.endsWith("/") ? man.Url : `${man.Url}/`;
    return `${base}${inst.Platform}/${inst.Architecture}/${inst.FileName}`;
  };

  const findBestInstaller = (installers: UpdateInstaller[], os: string, arch: string) => {
    const wantOs = normalize(os);
    const wantArch = normalize(arch);

    // Exact match
    let item = installers.find(i => normalize(i.Platform) === wantOs && normalize(i.Architecture) === wantArch);
    if (item) return item;

    // Arch preferences
    const archPrefs = [wantArch, "x64", "x86", "arm64", "arm"].filter(Boolean);
    for (const a of archPrefs) {
      item = installers.find(i => normalize(i.Platform) === wantOs && normalize(i.Architecture) === a);
      if (item) return item;
    }

    // Platform-only fallback
    item = installers.find(i => normalize(i.Platform) === wantOs);
    if (item) return item;

    // Global fallback
    return installers[0];
  };

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await fetch(product.manifestPath);
        if (cancelled) return;
        
        if (resp.ok) {
          const data = await resp.json() as UpdateManifest;
          setManifest(data);
          if (data.Installers && data.Installers.length > 0) {
            const best = findBestInstaller(data.Installers, detected.os, detected.arch);
            if (best) {
              setDownloadUrl(buildUrl(data, best));
              setSelectedInstaller(best);
            }
          }
        }
      } catch (e) {
        console.error(`Failed to load manifest for ${product.name}:`, e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [product, detected]);

  return (
    <TransparentCard>
      <CardHeader>
        <CardTitle>{product.name}</CardTitle>
        <CardDescription>{product.description}</CardDescription>
      </CardHeader>
      <CardContent>
        <Button asChild variant="default" disabled={loading || !downloadUrl}>
          <a
            href={downloadUrl ?? "#"}
            download
            className="flex items-center"
          >
            <Download className="h-4 w-4 mr-2" />
            {loading ? "Detecting..." : (downloadUrl ? `Download ${product.name}` : "No matching build found")}
          </a>
        </Button>

        <p className="text-sm text-muted-foreground mt-2">
          Detected: {detected.os} {detected.arch} (if this looks wrong, choose a manual download below)
        </p>

        <Accordion
          type="single"
          collapsible
          className="w-full AccordionRoot mt-4"
        >
          <AccordionItem value="File Information">
            <AccordionTrigger className="text-left">
              File Information
            </AccordionTrigger>
            <AccordionContent>
              {product.signature && (
                <p><span className="text-primary">Signature: </span>{product.signature}</p>
              )}
              {product.countersignature && (
                <p><span className="text-primary">Countersignature: </span>{product.countersignature}</p>
              )}
              <p><span className="text-primary">SHA-256: </span>{selectedInstaller?.SHA256 || "Not available"}</p>
              {manifest?.Version && (
                <p><span className="text-primary">Version: </span>{manifest.Version}</p>
              )}
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="Manual Downloads">
            <AccordionTrigger className="text-left">
              Manual Downloads
            </AccordionTrigger>
            <AccordionContent>
              {(!manifest || !manifest.Installers || manifest.Installers.length === 0) && (
                <p>No manual downloads found (manifest missing or empty).</p>
              )}
              {manifest && manifest.Installers && manifest.Installers.length > 0 && (
                <div className="space-y-2">
                  {manifest.Installers.map((inst, i) => {
                    const url = buildUrl(manifest, inst);
                    return (
                      <div key={i} className="flex items-center justify-between">
                        <div>
                          <div className="font-medium">{inst.FileName}</div>
                          <div className="text-sm text-muted-foreground">{inst.Platform} • {inst.Architecture}</div>
                          {inst.SHA256 && (
                            <div className="text-xs text-muted-foreground font-mono mt-0.5" title={inst.SHA256}>
                              SHA256: {inst.SHA256.substring(0, 12)}...
                            </div>
                          )}
                        </div>
                        <a href={url} download className="text-sm underline">
                          Download
                        </a>
                      </div>
                    );
                  })}
                </div>
              )}
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </CardContent>
    </TransparentCard>
  );
};

export default DownloadCard;
